import { TemplateValidationError } from 'src/models/dto/TemplateValidationError'
import { CONTAINER_COLUMN } from 'src/services/import-content/TemplateSpreadsheetService'

export enum ImportStep {
    Upload = 0,
    Review = 1,
    Create = 2,
}

export const IMPORT_STEP_LABELS: Record<ImportStep, string> = {
    [ImportStep.Upload]: 'Upload',
    [ImportStep.Review]: 'Review',
    [ImportStep.Create]: 'Create',
}

export const ROWS_PER_PAGE = 10

const MAX_CELL_CHARS = 40

export const truncateCell = (value?: string): string =>
    value && value.length > MAX_CELL_CHARS ? `${value.slice(0, MAX_CELL_CHARS)}…` : value ?? ''

export const ROW_BORDER = { borderBottom: '1px solid #E1E4EA' }

export const pluralize = (count: number, noun: string): string =>
    `${count} ${noun}${count === 1 ? '' : 's'}`

export interface ParsedSpreadsheet {
    headers: string[]
    rowCount: number
    containerCount: number
    rows: Record<string, string>[]
}

export interface SpreadsheetValidation {
    missing: string[]
    extra: string[]
}

export interface RowValidation {
    byRow: Record<number, string[]>
    general: string[]
}

const ROW_NUMBER = /^Row (\d+):\s*(.*)$/

const dedupe = (messages: string[]): string[] =>
    messages.filter((message, index) => messages.indexOf(message) === index)

export const FIRST_DATA_LINE = 2

export interface ContainerGroup {
    name: string
    rowIndices: number[]
}

export const groupRowsByContainer = (rows: Record<string, string>[]): ContainerGroup[] => {
    const rowIndicesByName = new Map<string, number[]>()
    rows.forEach((row, rowIndex) => {
        const name = (row[CONTAINER_COLUMN] ?? '').trim()
        const existing = rowIndicesByName.get(name)
        if (existing) {
            existing.push(rowIndex)
        } else {
            rowIndicesByName.set(name, [rowIndex])
        }
    })
    return Array.from(rowIndicesByName, ([name, rowIndices]) => ({ name, rowIndices }))
}

export const mapContainerErrorsToRows = (
    errors: TemplateValidationError[],
    rowIndices: number[]
): RowValidation => {
    const byRow: Record<number, string[]> = {}
    const general: string[] = []

    errors.forEach(({ message }) => {
        const match = ROW_NUMBER.exec(message)
        const rowIndex = match ? rowIndices[Number(match[1]) - 1] : undefined
        if (match && rowIndex !== undefined) {
            byRow[rowIndex] = dedupe([...(byRow[rowIndex] ?? []), match[2]])
        } else if (message) {
            general.push(message)
        }
    })

    return { byRow, general }
}

export const CONTAINER_VALIDATION_CONCURRENCY = 4

export const CONTAINER_VALIDATION_STAGGER_MS = 200

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const validateContainers = async (
    groups: number[][],
    validateGroup: (rowIndices: number[]) => Promise<RowValidation>,
    onRowsValidated: (completed: number) => void,
    concurrency: number = CONTAINER_VALIDATION_CONCURRENCY,
    staggerMs: number = CONTAINER_VALIDATION_STAGGER_MS
): Promise<RowValidation> => {
    const byRow: Record<number, string[]> = {}
    const general: string[] = []
    let nextIndex = 0
    let completed = 0

    const worker = async (workerIndex: number) => {
        if (workerIndex > 0) {
            await delay(workerIndex * staggerMs)
        }
        while (nextIndex < groups.length) {
            const rowIndices = groups[nextIndex++]
            try {
                const validated = await validateGroup(rowIndices)
                Object.assign(byRow, validated.byRow)
                general.push(...validated.general)
            } catch (e) {
                const message =
                    e instanceof Error ? e.message : 'These rows could not be validated.'
                rowIndices.forEach((rowIndex) => {
                    byRow[rowIndex] = [message]
                })
            }
            completed += rowIndices.length
            onRowsValidated(completed)
        }
    }

    await Promise.all(
        Array.from({ length: Math.min(concurrency, groups.length) }, (_unused, workerIndex) =>
            worker(workerIndex)
        )
    )

    return { byRow, general: dedupe(general) }
}

export const validateSpreadsheet = (
    spreadsheet: ParsedSpreadsheet,
    placeholders: string[]
): SpreadsheetValidation => {
    const headerSet = new Set(spreadsheet.headers)
    const placeholderSet = new Set(placeholders)

    return {
        missing: placeholders.filter((name) => !headerSet.has(name)),
        extra: spreadsheet.headers.filter((header) => !placeholderSet.has(header)),
    }
}
